import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const API_KEY = "5cb3971f2ed6c001df1ae772ee291eb2";

const ACTORS_PER_PAGE = 20;
const CACHE_TIME = 24 * 60 * 60 * 1000;

const getAge = (birthday) => {
  if (!birthday) return null;

  const birth = new Date(birthday);
  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();

  const month = today.getMonth() - birth.getMonth();

  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

function Actors() {
  const navigate = useNavigate();

  const [actors, setActors] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("");

  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [profession, setProfession] = useState("All");
  const [country, setCountry] = useState("All");
  const [gender, setGender] = useState("All");
  const [age, setAge] = useState("All");
  const [sortBy, setSortBy] = useState("Popular");

  // TMDB pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /*
  |--------------------------------------------------------------------------
  | Get cache for current page
  |--------------------------------------------------------------------------
  */

  const getCachedPage = (page) => {
    try {
      const cache = localStorage.getItem(`actors-page-${page}`);
      const cacheTime = localStorage.getItem(`actors-page-${page}-time`);

      if (cache && cacheTime && Date.now() - Number(cacheTime) < CACHE_TIME) {
        return JSON.parse(cache);
      }

      localStorage.removeItem(`actors-page-${page}`);
      localStorage.removeItem(`actors-page-${page}-time`);

      return null;
    } catch (error) {
      console.log("Cache error:", error);
      return null;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Save page cache
  |--------------------------------------------------------------------------
  */

  const saveCachedPage = (page, data) => {
    try {
      localStorage.setItem(`actors-page-${page}`, JSON.stringify(data));

      localStorage.setItem(`actors-page-${page}-time`, Date.now());
    } catch (error) {
      console.log("Cache save error:", error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Load one TMDB page
  |--------------------------------------------------------------------------
  */

  // const loadActors = async (page = 1) => {
  //   try {
  //     setLoading(true);
  //     setLoadingText("Loading actors...");

  //     /*
  //     |--------------------------------------------------------------------------
  //     | First check cache
  //     |--------------------------------------------------------------------------
  //     */

  //     const cached = getCachedPage(page);

  //     if (cached) {
  //       setActors(cached.actors);
  //       setTotalPages(cached.totalPages);
  //       setLoading(false);
  //       setLoadingText("");
  //       return;
  //     }

  //     /*
  //     |--------------------------------------------------------------------------
  //     | Get ONLY ONE page from TMDB
  //     |--------------------------------------------------------------------------
  //     */

  //     const response = await fetch(
  //       `https://api.themoviedb.org/3/person/popular?api_key=${API_KEY}&page=${page}`
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to fetch actors");
  //     }

  //     const data = await response.json();

  //     const people = data.results || [];

  //     setTotalPages(data.total_pages || 1);

  //     /*
  //     |--------------------------------------------------------------------------
  //     | Get details ONLY for these 20 actors
  //     |--------------------------------------------------------------------------
  //     */

  //     setLoadingText(`Loading ${people.length} actors...`);

  //     const fullPeople = await Promise.all(
  //       people.map(async (person) => {
  //         try {
  //           const detailsResponse = await fetch(
  //             `https://api.themoviedb.org/3/person/${person.id}?api_key=${API_KEY}`
  //           );

  //           const details = await detailsResponse.json();

  //           return {
  //             id: person.id,
  //             name: person.name,
  //             profile_path: person.profile_path,
  //             popularity: person.popularity,
  //             known_for_department: person.known_for_department,

  //             birthday: details.birthday || null,

  //             gender: details.gender || 0,

  //             country:
  //               details.place_of_birth?.split(",").pop()?.trim() || "Unknown",

  //             place_of_birth: details.place_of_birth || null,

  //             biography: details.biography || "",

  //             known_for: person.known_for || [],
  //           };
  //         } catch (error) {
  //           console.log(`Failed to load actor ${person.id}`, error);

  //           return {
  //             id: person.id,
  //             name: person.name,
  //             profile_path: person.profile_path,
  //             popularity: person.popularity,
  //             known_for_department: person.known_for_department,
  //             birthday: null,
  //             gender: 0,
  //             country: "Unknown",
  //             known_for: person.known_for || [],
  //           };
  //         }
  //       })
  //     );

  //     /*
  //     |--------------------------------------------------------------------------
  //     | Save page in cache
  //     |--------------------------------------------------------------------------
  //     */

  //     saveCachedPage(page, {
  //       actors: fullPeople,
  //       totalPages: data.total_pages || 1,
  //     });

  //     setActors(fullPeople);
  //   } catch (error) {
  //     console.log("Actors error:", error);
  //   } finally {
  //     setLoading(false);
  //     setLoadingText("");
  //   }
  // };

  const loadActors = async (page = 1, query = "") => {
    try {
      setLoading(true);
      setLoadingText("Loading actors...");

      /*
      |--------------------------------------------------------------------------
      | CACHE KEY
      |--------------------------------------------------------------------------
      */

      const cacheKey = query.trim()
        ? `actors-search-${query.trim().toLowerCase()}-page-${page}`
        : `actors-page-${page}`;

      const cacheTimeKey = `${cacheKey}-time`;

      /*
      |--------------------------------------------------------------------------
      | CHECK CACHE
      |--------------------------------------------------------------------------
      */

      try {
        const cache = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);

        if (cache && cacheTime && Date.now() - Number(cacheTime) < CACHE_TIME) {
          const cachedData = JSON.parse(cache);

          setActors(cachedData.actors);
          setTotalPages(cachedData.totalPages);

          setLoading(false);
          setLoadingText("");

          return;
        }
      } catch (error) {
        console.log("Cache error:", error);
      }

      /*
      |--------------------------------------------------------------------------
      | API URL
      |--------------------------------------------------------------------------
      */

      let url;

      if (query.trim() !== "") {
        url =
          `https://api.themoviedb.org/3/search/person` +
          `?api_key=${API_KEY}` +
          `&query=${encodeURIComponent(query.trim())}` +
          `&page=${page}` +
          `&include_adult=false`;
      } else {
        url =
          `https://api.themoviedb.org/3/person/popular` +
          `?api_key=${API_KEY}` +
          `&page=${page}`;
      }

      /*
      |--------------------------------------------------------------------------
      | FETCH ACTORS
      |--------------------------------------------------------------------------
      */

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch actors");
      }

      const data = await response.json();

      const people = data.results || [];

      setTotalPages(data.total_pages || 1);

      /*
      |--------------------------------------------------------------------------
      | LOAD DETAILS
      |--------------------------------------------------------------------------
      */

      setLoadingText(`Loading ${people.length} actors...`);

      const fullPeople = await Promise.all(
        people.map(async (person) => {
          try {
            const detailsResponse = await fetch(
              `https://api.themoviedb.org/3/person/${person.id}?api_key=${API_KEY}`
            );

            const details = await detailsResponse.json();

            return {
              id: person.id,

              name: person.name,

              profile_path: person.profile_path,

              popularity: person.popularity,

              known_for_department: person.known_for_department,

              birthday: details.birthday || null,

              gender: details.gender || 0,

              country:
                details.place_of_birth?.split(",").pop()?.trim() || "Unknown",

              place_of_birth: details.place_of_birth || null,

              biography: details.biography || "",

              known_for: person.known_for || [],
            };
          } catch (error) {
            console.log(`Failed to load actor ${person.id}`, error);

            return {
              id: person.id,

              name: person.name,

              profile_path: person.profile_path,

              popularity: person.popularity,

              known_for_department: person.known_for_department,

              birthday: null,

              gender: 0,

              country: "Unknown",

              known_for: person.known_for || [],
            };
          }
        })
      );

      /*
      |--------------------------------------------------------------------------
      | SAVE CACHE
      |--------------------------------------------------------------------------
      */

      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            actors: fullPeople,
            totalPages: data.total_pages || 1,
          })
        );

        localStorage.setItem(cacheTimeKey, Date.now());
      } catch (error) {
        console.log("Cache save error:", error);
      }

      setActors(fullPeople);
    } catch (error) {
      console.log("Actors error:", error);

      setActors([]);
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Load current page
  |--------------------------------------------------------------------------
  */

  // useEffect(() => {
  //   loadActors(currentPage);
  // }, [currentPage]);

  useEffect(() => {
    loadActors(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  /*
  |--------------------------------------------------------------------------
  | Countries
  |--------------------------------------------------------------------------
  */

  const countries = useMemo(() => {
    const countryList = actors
      .map((actor) => actor.country)
      .filter((country) => country && country !== "Unknown");

    return ["All", ...new Set(countryList)].sort();
  }, [actors]);

  /*
  |--------------------------------------------------------------------------
  | Filter current page
  |--------------------------------------------------------------------------
  */

  const filteredActors = useMemo(() => {
    let data = [...actors];

    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    if (search.trim() !== "") {
      const searchValue = search.trim().toLowerCase();

      data = data.filter((actor) =>
        actor.name?.toLowerCase().includes(searchValue)
      );
    }

    /*
    |--------------------------------------------------------------------------
    | Profession
    |--------------------------------------------------------------------------
    */

    if (profession !== "All") {
      data = data.filter((actor) => actor.known_for_department === profession);
    }

    /*
    |--------------------------------------------------------------------------
    | Country
    |--------------------------------------------------------------------------
    */

    if (country !== "All") {
      data = data.filter((actor) => actor.country === country);
    }

    /*
    |--------------------------------------------------------------------------
    | Gender
    |--------------------------------------------------------------------------
    */

    if (gender !== "All") {
      data = data.filter((actor) => {
        if (gender === "Male") {
          return actor.gender === 2;
        }

        if (gender === "Female") {
          return actor.gender === 1;
        }

        return true;
      });
    }

    /*
    |--------------------------------------------------------------------------
    | Age
    |--------------------------------------------------------------------------
    */

    if (age !== "All") {
      data = data.filter((actor) => {
        const actorAge = getAge(actor.birthday);

        if (actorAge === null) {
          return false;
        }

        switch (age) {
          case "18-30":
            return actorAge >= 18 && actorAge <= 30;

          case "31-40":
            return actorAge >= 31 && actorAge <= 40;

          case "41-50":
            return actorAge >= 41 && actorAge <= 50;

          case "51-60":
            return actorAge >= 51 && actorAge <= 60;

          case "60+":
            return actorAge >= 60;

          default:
            return true;
        }
      });
    }

    /*
    |--------------------------------------------------------------------------
    | Sorting
    |--------------------------------------------------------------------------
    */

    switch (sortBy) {
      case "Popular":
        data.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
        break;

      case "Name A-Z":
        data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;

      case "Name Z-A":
        data.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;

      case "Youngest":
        data.sort(
          (a, b) => (getAge(a.birthday) ?? 999) - (getAge(b.birthday) ?? 999)
        );
        break;

      case "Oldest":
        data.sort(
          (a, b) => (getAge(b.birthday) ?? 0) - (getAge(a.birthday) ?? 0)
        );
        break;

      default:
        break;
    }

    return data;
  }, [actors, search, profession, country, gender, age, sortBy]);

  /*
  |--------------------------------------------------------------------------
  | Change page
  |--------------------------------------------------------------------------
  */

  const goToPage = (page) => {
    if (page < 1) return;
    if (page > totalPages) return;

    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
  |--------------------------------------------------------------------------
  | Search
  |--------------------------------------------------------------------------
  */

  // const handleSearch = async (e) => {
  //   e.preventDefault();

  //   /*
  //     If you want real TMDB actor search,
  //     we will handle that separately below.
  //   */

  //   setCurrentPage(1);
  // };

  const handleSearch = (e) => {
    e.preventDefault();

    const query = search.trim();

    setCurrentPage(1);
    setSearchQuery(query);
  };

  /*
  |--------------------------------------------------------------------------
  | Reset filters
  |--------------------------------------------------------------------------
  */

  // const resetFilters = () => {
  //   setSearch("");
  //   setProfession("All");
  //   setCountry("All");
  //   setGender("All");
  //   setAge("All");
  //   setSortBy("Popular");
  //   setCurrentPage(1);
  // };

  const resetFilters = () => {
    setSearch("");
    setSearchQuery("");

    setProfession("All");
    setCountry("All");
    setGender("All");
    setAge("All");
    setSortBy("Popular");

    setCurrentPage(1);
  };

  return (
    <ActorsPage>
      {/* FILTERS */}

      <Filters>
        {/* <SearchForm onSubmit={handleSearch}>
          <SearchInput
            type="text"
            placeholder="Search actors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <SearchButton type="submit">Search</SearchButton>
        </SearchForm> */}
        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            type="text"
            placeholder="Search actors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <SearchButton type="submit">Search</SearchButton>
        </SearchForm>

        <Select
          value={profession}
          onChange={(e) => {
            setProfession(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All Professions</option>
          <option value="Acting">Acting</option>
          <option value="Directing">Directing</option>
          <option value="Writing">Writing</option>
        </Select>

        <Select
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
            setCurrentPage(1);
          }}
        >
          {countries.map((item) => (
            <option value={item} key={item}>
              {item}
            </option>
          ))}
        </Select>

        <Select
          value={gender}
          onChange={(e) => {
            setGender(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </Select>

        <Select
          value={age}
          onChange={(e) => {
            setAge(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All Ages</option>
          <option value="18-30">18 - 30</option>
          <option value="31-40">31 - 40</option>
          <option value="41-50">41 - 50</option>
          <option value="51-60">51 - 60</option>
          <option value="60+">60+</option>
        </Select>

        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="Popular">Popular</option>

          <option value="Name A-Z">Name A-Z</option>

          <option value="Name Z-A">Name Z-A</option>

          <option value="Youngest">Youngest</option>

          <option value="Oldest">Oldest</option>
        </Select>

        <ResetButton onClick={resetFilters}>Reset</ResetButton>
      </Filters>

      {/* ACTORS */}

      {loading ? (
        <Loading>
          <LoadingSpinner />

          <LoadingText>{loadingText || "Loading actors..."}</LoadingText>
        </Loading>
      ) : (
        <ActorsContainer>
          {filteredActors.length > 0 ? (
            filteredActors.map((actor) => (
              <ActorCard
                key={actor.id}
                onClick={() => navigate(`/actor-details/${actor.id}`)}
              >
                <ImageWrapper>
                  <ActorImage
                    className="actor-image"
                    src={
                      actor.profile_path
                        ? `https://image.tmdb.org/t/p/w500${actor.profile_path}`
                        : "/no-profile.png"
                    }
                    alt={actor.name}
                    loading="lazy"
                  />
                </ImageWrapper>

                <ActorInfo>
                  <ActorName className="actor-name">{actor.name}</ActorName>

                  <ActorAge>
                    {actor.birthday
                      ? `${getAge(actor.birthday)} Years`
                      : "Age unknown"}
                  </ActorAge>
                </ActorInfo>
              </ActorCard>
            ))
          ) : (
            <NoResults>No actors found.</NoResults>
          )}
        </ActorsContainer>
      )}

      {/* PAGINATION */}

      {!loading && (
        <Pagination>
          <PaginationButton
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Previous
          </PaginationButton>

          <PageNumber>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </PageNumber>

          <PaginationButton
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next
          </PaginationButton>
        </Pagination>
      )}
    </ActorsPage>
  );
}

/* =========================================================
   PAGE
========================================================= */

export const ActorsPage = styled.div`
  min-height: 100vh;
  padding: 20px 5% 80px;
  // background: #0b0b0b;
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px 20px 60px;
  }
`;

/* =========================================================
   FILTERS SECTION
========================================================= */

export const Filters = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto 40px;

  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  background: #111;
  border: 1px solid rgba(184, 149, 100, 0.15);
  border-radius: 12px;
  padding: 18px;

  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    padding: 15px;
    gap: 10px;
  }
`;

/* =========================================================
   SEARCH
========================================================= */

export const SearchForm = styled.form`
  display: flex;
  flex: 1;
  min-width: 240px;
  height: 45px;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;

  background: #181818;
  border: 1px solid #292929;
  border-right: none;

  color: #fff;
  padding: 0 15px;

  font-size: 14px;
  outline: none;

  border-radius: 7px 0 0 7px;

  transition: 0.3s;

  &::placeholder {
    color: #777;
  }

  &:focus {
    border-color: #b89564;
    box-shadow: 0 0 0 1px rgba(184, 149, 100, 0.15);
  }
`;

export const SearchButton = styled.button`
  height: 45px;

  padding: 0 20px;

  border: 1px solid #b89564;
  background: #b89564;
  color: #111;

  font-size: 14px;
  font-weight: 600;

  border-radius: 0 7px 7px 0;

  cursor: pointer;

  transition: 0.3s;

  &:hover {
    background: #caa875;
    border-color: #caa875;
  }
`;

/* =========================================================
   SELECT
========================================================= */

export const Select = styled.select`
  height: 45px;
  min-width: 130px;

  padding: 0 35px 0 13px;

  background: #181818;
  color: #ddd;

  border: 1px solid #292929;
  border-radius: 7px;

  outline: none;

  font-size: 13px;

  cursor: pointer;

  transition: 0.3s;

  &:hover {
    border-color: rgba(184, 149, 100, 0.6);
  }

  &:focus {
    border-color: #b89564;
  }

  option {
    background: #181818;
    color: #fff;
  }

  @media (max-width: 900px) {
    flex: 1;
  }

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
  }
`;

/* =========================================================
   RESET BUTTON
========================================================= */

export const ResetButton = styled.button`
  height: 45px;

  padding: 0 18px;

  border: 1px solid #333;
  border-radius: 7px;

  background: transparent;
  color: #aaa;

  font-size: 13px;

  cursor: pointer;

  transition: 0.3s;

  &:hover {
    color: #fff;
    border-color: #b89564;
    background: rgba(184, 149, 100, 0.08);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

/* =========================================================
   ACTORS CONTAINER
========================================================= */

export const ActorsContainer = styled.div`
  width: 100%;
  max-width: 1500px;

  margin: 0 auto;

  display: grid;

  grid-template-columns: repeat(6, 1fr);

  gap: 25px 18px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px 12px;
  }

  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 12px;
  }
`;

/* =========================================================
   ACTOR CARD
========================================================= */

export const ActorCard = styled.div`
  min-width: 0;

  cursor: pointer;

  overflow: hidden;

  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);

    .actor-image {
      transform: scale(1.05);
    }

    .actor-name {
      color: #b89564;
    }
  }
`;

/* =========================================================
   IMAGE
========================================================= */

export const ImageWrapper = styled.div`
  position: relative;

  width: 100%;

  aspect-ratio: 2 / 3;

  overflow: hidden;

  border-radius: 8px;

  background: #161616;

  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);

  &::after {
    content: "";

    position: absolute;
    inset: 0;

    background: linear-gradient(to top, rgba(0, 0, 0, 0.35), transparent 40%);

    pointer-events: none;
  }
`;

export const ActorImage = styled.img`
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition: transform 0.5s ease;
`;

/* =========================================================
   ACTOR INFO
========================================================= */

export const ActorInfo = styled.div`
  padding: 10px 2px 0;
`;

export const ActorName = styled.h3`
  margin: 0;

  color: #fff;

  font-size: 15px;
  font-weight: 600;

  line-height: 1.4;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: color 0.3s ease;
`;

export const ActorAge = styled.p`
  margin: 5px 0 0;

  color: #777;

  font-size: 12px;

  line-height: 1.4;
`;

/* =========================================================
   LOADING
========================================================= */

export const Loading = styled.div`
  width: 100%;
  min-height: 400px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  color: #aaa;

  font-size: 14px;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;

  margin-bottom: 15px;

  border: 3px solid #292929;
  border-top-color: #b89564;

  border-radius: 50%;

  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  margin: 0;

  color: #888;

  font-size: 13px;
`;

/* =========================================================
   NO RESULTS
========================================================= */

export const NoResults = styled.div`
  grid-column: 1 / -1;

  min-height: 300px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #777;

  font-size: 14px;

  text-align: center;
`;

/* =========================================================
   PAGINATION
========================================================= */

export const Pagination = styled.div`
  max-width: 1500px;

  margin: 50px auto 0;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 20px;
`;

export const PaginationButton = styled.button`
  min-width: 100px;
  height: 42px;

  padding: 0 18px;

  border-radius: 6px;

  border: 1px solid #333;

  background: #151515;
  color: #ddd;

  font-size: 13px;

  cursor: pointer;

  transition: 0.3s;

  &:hover:not(:disabled) {
    border-color: #b89564;
    color: #b89564;
    background: rgba(184, 149, 100, 0.06);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const PageNumber = styled.span`
  min-width: 110px;

  text-align: center;

  color: #999;

  font-size: 13px;

  strong {
    color: #b89564;
    font-weight: 600;
  }
`;

export default Actors;
